// ============================================================================
// Feuille du noyau : ARITHMÉTIQUE — algorithme d'Euclide et relation de Bézout
// (refonte, lot 5.10, moteur v35).
// ============================================================================
// Deux objectifs :
//
//   `gcd`     « PGCD(1071 ; 462) » → « PGCD(1071 ; 462) = 21 »
//   `bezout`  « 17u + 5v = 1 »     → « u = −2 ; v = 7 »
//
// et, depuis le moteur v51 (lot 6.54), deux objectifs PAR LES DÉCOMPOSITIONS en facteurs
// premiers : `gcd_primes` (« PGCD(84 ; 126) » → « PGCD(84 ; 126) = 42 ») et `lcm`
// (« PPCM(12 ; 18) » → « PPCM(12 ; 18) = 36 ») : décomposer chaque nombre
// (« 84 = 2² × 3 × 7 »), garder les facteurs communs au plus petit exposant (PGCD) ou tous
// au plus grand (PPCM), puis calculer.
//
// L'élève écrit les divisions euclidiennes (« 1071 = 2 × 462 + 147 »), puis conclut
// (« PGCD(1071 ; 462) = 21 », ou la chaîne « PGCD(1071 ; 462) = PGCD(462 ; 147) = … ») ;
// pour Bézout, il remonte l'algorithme : il isole le dernier reste non nul
// (« 1 = 5 − 2 × 2 »), remplace un reste par son expression (« 1 = 5 − 2 × (17 − 3 × 5) »),
// regroupe (« 1 = 7 × 5 − 2 × 17 »), puis lit le couple (« u = −2 ; v = 7 »).
//
//   Division euclidienne                                     2   arith.division@1
//   PGCD(a ; b) = PGCD(b ; r) (la division écrite)           1   arith.gcd_step@1
//   PGCD(a ; 0) = a                                          1   arith.gcd_zero@1
//   b divise a : PGCD(a ; b) = b (la division écrite)        1   arith.gcd_divides@1
//   Le PGCD est le dernier reste non nul                     1   arith.gcd_last@1
//   Isoler le reste                                          1   arith.isolate@1
//   Remplacer un reste par son expression                    2   arith.substitute@1
//   Développer                                               1   arith.expand@1
//   Regrouper                                                1   arith.regroup@1
//   Lire les coefficients                                    1   arith.read@1
//   Décomposer en facteurs premiers (v51)                    2   arith.primes@1
//   Les facteurs communs, au plus petit exposant (v51)       2   arith.gcd_primes@1
//   Tous les facteurs, au plus grand exposant (v51)          2   arith.lcm_primes@1
//   Calculer le produit (v51)                                1   arith.compute@1
//
// Calcul exact (BigInt). Une ligne juste qui saute des étapes reste sur la feuille, sans
// HORA (D7) ; une ligne fausse ne dit jamais la bonne valeur.
// ============================================================================

import type { Chip } from './lexer';
import type { ScratchModel } from './model';
import type { Line } from './tree';
import {
  ArithIncomplete,
  ArithSyntaxError,
  absB,
  arithLineAtoms,
  arithText,
  bareNumber,
  combinations,
  euclid,
  evalA,
  exprText,
  gcdAtom,
  combineFactors,
  gcdB,
  hasGcd,
  hasLcm,
  hasParenSum,
  isGcdAtom,
  isLcmAtom,
  isSupAtom,
  lcmAtom,
  lcmB,
  primeProductOf,
  sameFactors,
  writtenFactors,
  linKey,
  numText,
  parseArithLine,
  substituteLin,
  symsOf,
  topTerms,
  type AChain,
  type AExpr,
  type Euclid,
  type LinForm,
} from './kernel/arith';

export const ARITH_STEP_IDS = {
  division: 'arith.division@1',
  gcdStep: 'arith.gcd_step@1',
  gcdZero: 'arith.gcd_zero@1',
  gcdDivides: 'arith.gcd_divides@1',
  gcdLast: 'arith.gcd_last@1',
  isolate: 'arith.isolate@1',
  substitute: 'arith.substitute@1',
  expand: 'arith.expand@1',
  regroup: 'arith.regroup@1',
  read: 'arith.read@1',
  decompose: 'arith.primes@1',
  gcdPrimes: 'arith.gcd_primes@1',
  lcmPrimes: 'arith.lcm_primes@1',
  compute: 'arith.compute@1',
} as const;
type StepKind = keyof typeof ARITH_STEP_IDS;

const STEP: Record<StepKind, { label: string; hora: number; why: string }> = {
  division: {
    label: 'Division euclidienne',
    hora: 2,
    why: 'a = q × b + r, avec un reste r positif et plus petit que b.',
  },
  gcdStep: {
    label: 'PGCD(a ; b) = PGCD(b ; r)',
    hora: 1,
    why: 'Les diviseurs communs de a et b sont ceux de b et du reste r : PGCD(a ; b) = PGCD(b ; r).',
  },
  gcdZero: { label: 'PGCD(a ; 0) = a', hora: 1, why: 'Tout nombre divise 0 : PGCD(a ; 0) = a.' },
  gcdDivides: {
    label: 'b divise a',
    hora: 1,
    why: 'Le reste est nul : b divise a, donc PGCD(a ; b) = b.',
  },
  gcdLast: {
    label: 'Le dernier reste non nul',
    hora: 1,
    why: 'Le PGCD est le dernier reste non nul de l’algorithme d’Euclide.',
  },
  isolate: {
    label: 'Isoler le reste',
    hora: 1,
    why: 'De a = q × b + r, on tire r = a − q × b.',
  },
  substitute: {
    label: 'Remplacer un reste',
    hora: 2,
    why: 'On remplace un reste par son expression tirée de la division qui l’a donné, entre parenthèses.',
  },
  expand: { label: 'Développer', hora: 1, why: 'On développe le produit par la parenthèse.' },
  regroup: {
    label: 'Regrouper',
    hora: 1,
    why: 'On regroupe les termes en chacun des deux nombres du départ.',
  },
  decompose: {
    label: 'Décomposer en facteurs premiers',
    hora: 2,
    why: 'On écrit le nombre comme un produit de nombres premiers, avec des puissances.',
  },
  gcdPrimes: {
    label: 'Les facteurs communs',
    hora: 2,
    why: 'Le PGCD : les facteurs premiers communs aux deux décompositions, chacun avec le plus petit exposant.',
  },
  lcmPrimes: {
    label: 'Tous les facteurs',
    hora: 2,
    why: 'Le PPCM : tous les facteurs premiers des deux décompositions, chacun avec le plus grand exposant.',
  },
  compute: { label: 'Calculer', hora: 1, why: 'On calcule le produit.' },
  read: {
    label: 'Lire les coefficients',
    hora: 1,
    why: 'Les coefficients de l’égalité de Bézout donnent un couple solution.',
  },
};

const CARD = 'Arithmétique';

export interface KArithVerdict {
  kind:
    | 'empty'
    | 'open'
    | 'building'
    | 'ready'
    | 'pending'
    | 'syntax'
    | 'trap'
    | 'false'
    | 'unjustified'
    | 'useless'
    | 'step';
  atoms: string[];
  title?: string;
  message?: string;
  badChip?: number;
  complete?: boolean;
  line?: Line;
  final?: boolean;
  hora?: number;
  /** D7 : ligne juste qui saute des étapes, gardée sans HORA */
  jump?: boolean;
  trap?: { line: Line; title: string; why: string };
  rule?: { id: string; card: string; label: string; hora: number; why: string };
  amove?: AMove;
}

/** ce que la ligne apprend au moteur */
interface AMove {
  text: string;
  divisions?: number[];
  /** combinaisons du PGCD établies (forme linéaire sur la suite, mesure de l'écriture) */
  combos?: { lin: LinForm; measure: number }[];
  /** reste isolé (division k) */
  isolated?: number[];
  /** PGCD de la question établi */
  gcdKnown?: boolean;
  /** moteur v51 : la décomposition d'un des deux nombres */
  dec?: [bigint, Map<bigint, number>];
  /** moteur v51 : le PGCD (PPCM) écrit en facteurs premiers */
  primesForm?: boolean;
  assign?: [string, bigint][];
  final?: boolean;
}

export interface KArithState {
  model: ScratchModel;
  /** `gcd` aussi pour `gcd_primes` (moteur v51), `lcm` : le PPCM (moteur v51) */
  goal: 'gcd' | 'bezout' | 'lcm';
  /** moteur v51 : PGCD / PPCM par les décompositions en facteurs premiers */
  primes: boolean;
  /** les deux nombres de la question (valeurs absolues) */
  a: bigint;
  b: bigint;
  /** le PPCM des deux nombres */
  L: bigint;
  /** les décompositions écrites (nombre → premier → exposant) */
  dec: Map<string, Map<bigint, number>>;
  /** le PGCD (PPCM) de la question est écrit en facteurs premiers */
  primesForm: boolean;
  /** la suite d'Euclide des deux nombres de la question */
  eu: Euclid;
  /** valeur → indice dans la suite (sans le 0 final) */
  idx: Map<bigint, number>;
  d: bigint;
  /** le PGCD de la question, tel qu'écrit au départ */
  target: string;
  /** Bézout : l'équation a·u + b·v = d */
  eq: { coef: Map<string, bigint>; rhs: bigint; unknowns: string[] } | null;
  /** divisions k écrites (r[k−1] = q[k] × r[k] + r[k+1]) */
  divisions: Set<number>;
  isolated: Set<number>;
  /** la dernière combinaison écrite pour le PGCD */
  cur: { lin: LinForm; measure: number } | null;
  /** les combinaisons établies (clés) */
  combos: Set<string>;
  /** les égalités de Bézout établies (sur les deux nombres du départ seulement) */
  bez: LinForm[];
  assigned: Map<string, bigint>;
  gcdKnown: boolean;
  concluded: boolean;
  written: Set<string>;
  givenTexts: Set<string>;
  path: string[];
  traps: { text: string; line: Line; title: string; why: string }[];
  lines: number;
}

/* ------------------------------ lecture ------------------------------ */

export function karithLineAtoms(text: string): string[] {
  return arithLineAtoms(text);
}
const lineOf = (atoms: string[]): Line => ({
  dis: [{ sides: [{ k: 's', n: arithText(atoms) }] }],
});
/** Ligne de feuille (texte) pour l'app : l'écriture de l'élève, mise en forme. */
export function karithLine(text: string): Line {
  return lineOf(karithLineAtoms(text));
}
const canonOf = (text: string): string => {
  try {
    return arithText(arithLineAtoms(text));
  } catch {
    return text.trim();
  }
};

const strip = (e: AExpr): AExpr => (e.k === 'paren' ? strip(e.e) : e);
const gcdOf = (e: AExpr): { a: bigint; b: bigint } | null => {
  const s = strip(e);
  return s.k === 'gcd' ? { a: s.a, b: s.b } : null;
};
const lcmOf = (e: AExpr): { a: bigint; b: bigint } | null => {
  const s = strip(e);
  return s.k === 'lcm' ? { a: s.a, b: s.b } : null;
};

/** forme linéaire en lettres d'une expression : 17u + 5v → {u: 17, v: 5}, constante */
function symLinear(e: AExpr): { coef: Map<string, bigint>; c: bigint } | null {
  switch (e.k) {
    case 'num':
      return { coef: new Map(), c: e.v };
    case 'sym':
      return { coef: new Map([[e.n, 1n]]), c: 0n };
    case 'paren':
      return symLinear(e.e);
    case 'neg': {
      const r = symLinear(e.e);
      if (!r) return null;
      return { coef: new Map([...r.coef].map(([k, v]) => [k, -v])), c: -r.c };
    }
    case 'add': {
      const coef = new Map<string, bigint>();
      let c = 0n;
      for (const t of e.t) {
        const r = symLinear(t.e);
        if (!r) return null;
        r.coef.forEach((v, k) => coef.set(k, (coef.get(k) ?? 0n) + BigInt(t.s) * v));
        c += BigInt(t.s) * r.c;
      }
      return { coef, c };
    }
    case 'mul': {
      let acc: { coef: Map<string, bigint>; c: bigint } = { coef: new Map(), c: 1n };
      for (const f of e.f) {
        const r = symLinear(f);
        if (!r) return null;
        if (acc.coef.size && r.coef.size) return null;
        if (acc.coef.size)
          acc = { coef: new Map([...acc.coef].map(([k, v]) => [k, v * r.c])), c: acc.c * r.c };
        else if (r.coef.size)
          acc = { coef: new Map([...r.coef].map(([k, v]) => [k, v * acc.c])), c: r.c * acc.c };
        else acc = { coef: new Map(), c: acc.c * r.c };
      }
      return acc;
    }
    case 'gcd':
    case 'lcm':
      return null;
    case 'pow': {
      const v = evalA(e);
      return v === null ? null : { coef: new Map(), c: v };
    }
  }
}

/** la question : le PGCD (« PGCD(1071 ; 462) ») ou l'équation (« 17u + 5v = 1 ») */
export function arithQuestionOf(model: ScratchModel): {
  goal: 'gcd' | 'bezout' | 'lcm';
  primes: boolean;
  a: bigint;
  b: bigint;
  eq: KArithState['eq'];
} {
  const spec = model.kernel!;
  const line = parseArithLine(arithLineAtoms(spec.start));
  if (line.parts.length !== 1) throw new ArithSyntaxError('Le départ est une seule écriture.');
  const ch = line.parts[0]!;
  const kind = spec.goal.kind;
  if (kind === 'gcd' || kind === 'gcd_primes') {
    const g = ch.members.length === 1 ? gcdOf(ch.members[0]!) : null;
    if (!g) throw new ArithSyntaxError('Le départ d’un PGCD s’écrit « PGCD(a ; b) ».');
    return { goal: 'gcd', primes: kind === 'gcd_primes', a: absB(g.a), b: absB(g.b), eq: null };
  }
  if (kind === 'lcm') {
    const g = ch.members.length === 1 ? lcmOf(ch.members[0]!) : null;
    if (!g) throw new ArithSyntaxError('Le départ d’un PPCM s’écrit « PPCM(a ; b) ».');
    return { goal: 'lcm', primes: true, a: absB(g.a), b: absB(g.b), eq: null };
  }
  if (ch.members.length !== 2 || ch.rels[0] !== '=' || ch.mod !== null)
    throw new ArithSyntaxError('Le départ de Bézout s’écrit « au + bv = d ».');
  const lhs = symLinear(ch.members[0]!);
  const rhs = bareNumber(ch.members[1]!);
  if (!lhs || rhs === null || lhs.c !== 0n || lhs.coef.size !== 2)
    throw new ArithSyntaxError('Le départ de Bézout s’écrit « au + bv = d », deux inconnues.');
  const unknowns = [...lhs.coef.keys()];
  const [a, b] = unknowns.map((u) => lhs.coef.get(u)!);
  if (!a || !b) throw new ArithSyntaxError('Les deux coefficients sont non nuls.');
  return {
    goal: 'bezout',
    primes: false,
    a: absB(a),
    b: absB(b),
    eq: { coef: lhs.coef, rhs, unknowns },
  };
}

/* ------------------------------ état ------------------------------ */

export function createKArithState(model: ScratchModel): KArithState {
  const spec = model.kernel!;
  const q = arithQuestionOf(model);
  if (q.a === 0n || q.b === 0n) throw new ArithSyntaxError('Deux nombres non nuls.');
  const eu = euclid(q.a, q.b);
  const idx = new Map<bigint, number>();
  eu.r.forEach((v, i) => {
    if (v !== 0n && !idx.has(v)) idx.set(v, i);
  });
  const d = eu.r[eu.m]!;
  const traps = (model.traps ?? []).map((t) => ({
    text: canonOf(t.line),
    line: karithLine(t.line),
    title: t.title ?? 'Attention',
    why: t.why,
  }));
  const givenTexts = new Set([canonOf(spec.start), ...(model.givens ?? []).map(canonOf)]);
  // moteur v51 : une décomposition donnée par l'énoncé (« 360 = 2³ × 3² × 5 ») est connue
  const dec = new Map<string, Map<bigint, number>>();
  if (q.primes)
    for (const g of model.givens ?? []) {
      const found = readDecomposition(g, q.a, q.b);
      if (found) dec.set(String(found[0]), found[1]);
    }
  return {
    model,
    goal: q.goal,
    primes: q.primes,
    a: q.a,
    b: q.b,
    L: lcmB(q.a, q.b),
    dec,
    primesForm: false,
    eu,
    idx,
    d,
    target: q.goal === 'lcm' ? lcmAtom(q.a, q.b) : gcdAtom(eu.r[0]!, eu.r[1]!),
    eq: q.eq,
    divisions: new Set(),
    isolated: new Set(),
    cur: null,
    combos: new Set(),
    bez: [],
    assigned: new Map(),
    gcdKnown: false,
    concluded: false,
    written: new Set(),
    givenTexts,
    path: spec.path ?? [],
    traps,
    lines: 0,
  };
}

/* ------------------------------ verdicts ------------------------------ */

type Base = { atoms: string[]; line: Line };

function stepVerdict(
  base: Base,
  steps: StepKind[],
  amove: AMove,
  final: boolean,
  extra = '',
): KArithVerdict {
  const hora = steps.reduce((s, k) => s + STEP[k].hora, 0);
  const main = steps.reduce((m, k) => (STEP[k].hora > STEP[m].hora ? k : m), steps[0]!);
  const why =
    [...new Set(steps)].map((k) => STEP[k].why).join(' ') +
    extra +
    (final ? ' C’est la réponse.' : '');
  return {
    kind: 'step',
    ...base,
    hora,
    final,
    rule: { id: ARITH_STEP_IDS[main], card: CARD, label: STEP[main].label, hora, why },
    amove: { ...amove, ...(final ? { final } : {}) },
  };
}

function jumpVerdict(base: Base, amove: AMove, final: boolean): KArithVerdict {
  return {
    kind: 'useless',
    ...base,
    jump: true,
    final,
    title: final ? 'Juste !' : 'Juste, mais tu sautes des étapes',
    message: final
      ? 'C’est la bonne réponse. Détaille ton raisonnement, une étape par ligne, pour gagner des HORA. Tu peux garder cette ligne sans HORA.'
      : 'Ta ligne est juste. Détaille-la, une étape par ligne, pour gagner des HORA. Tu peux la garder sans HORA.',
    amove: { ...amove, ...(final ? { final } : {}) },
  };
}

const aside = (base: Base, message?: string): KArithVerdict => ({
  kind: 'useless',
  ...base,
  title: 'Juste, mais à côté',
  message: message ?? 'C’est juste, mais ça ne sert pas pour la question.',
});

const nothingNew = (base: Base, message?: string): KArithVerdict => ({
  kind: 'useless',
  ...base,
  title: 'Rien de nouveau',
  message: message ?? 'Cette ligne redit ce qui est déjà écrit.',
});

function trapOr(
  ks: KArithState,
  base: Base,
  why: { title: string; message: string },
): KArithVerdict {
  const text = arithText(base.atoms);
  const t = ks.traps.find((t) => t.text === text);
  if (t)
    return {
      kind: 'trap',
      ...base,
      title: t.title,
      message: t.why,
      trap: { line: t.line, title: t.title, why: t.why },
    };
  return { kind: 'false', ...base, ...why };
}

/* ------------------------------ juge ------------------------------ */

function strictPrefixOfKnown(ks: KArithState, A: string[]): boolean {
  return ks.path.some((t) => {
    let atoms: string[];
    try {
      atoms = arithLineAtoms(t);
    } catch {
      return false;
    }
    return atoms.length > A.length && A.every((a, i) => atoms[i] === a);
  });
}

export function karithJudge(
  ks: KArithState,
  chips: Chip[],
  opts?: { verify?: boolean },
): KArithVerdict {
  const A = chips.flatMap((c) => c.atoms);
  if (!A.length) return { kind: 'empty', atoms: A, badChip: -1 };
  const prefix = strictPrefixOfKnown(ks, A);
  let parts: AChain[];
  try {
    parts = parseArithLine(A).parts;
  } catch (e) {
    if (e instanceof ArithIncomplete || (prefix && !opts?.verify)) {
      if (opts?.verify)
        return { kind: 'syntax', atoms: A, title: 'Ligne inachevée', message: 'Termine ta ligne.' };
      return { kind: prefix ? 'building' : 'open', atoms: A, complete: false };
    }
    const couple = A.includes(';') && A.includes('(');
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Ça ne se lit pas',
      message: couple
        ? 'Écris un couple ainsi : « u = … ; v = … ».'
        : e instanceof ArithSyntaxError
          ? e.message
          : String(e),
      badChip: chips.length - 1,
    };
  }
  if (parts.some((p) => p.members.length < 2)) {
    if (opts?.verify)
      return {
        kind: 'syntax',
        atoms: A,
        line: lineOf(A),
        title: 'Une égalité',
        message: 'Écris une égalité : « … = … ».',
      };
    return { kind: prefix ? 'building' : 'open', atoms: A, complete: false };
  }
  const v = judgeParsed(ks, A, parts);
  if (prefix && !opts?.verify) {
    if (v.kind === 'step') return { ...v, kind: 'ready', complete: true };
    return { kind: 'building', atoms: A, complete: true };
  }
  if (!opts?.verify && (v.kind === 'unjustified' || v.kind === 'false'))
    return { kind: 'pending', atoms: A, complete: true };
  return { ...v, complete: true };
}

function judgeParsed(ks: KArithState, A: string[], parts: AChain[]): KArithVerdict {
  const line = lineOf(A);
  const base: Base = { atoms: A, line };
  const text = arithText(A);
  if (parts.some((p) => p.rels.includes('≡') || p.mod !== null))
    return {
      kind: 'syntax',
      ...base,
      title: 'Pas de congruence ici',
      message: 'Dans cette feuille, on écrit des égalités « = » entre entiers.',
    };
  if (ks.givenTexts.has(text))
    return {
      kind: 'useless',
      ...base,
      title: 'C’est l’énoncé',
      message: 'Cette ligne est déjà dans l’énoncé : écris le calcul suivant.',
    };
  if (ks.written.has(text)) return nothingNew(base, 'Cette ligne est déjà sur ta feuille.');

  // « u = −2 ; v = 7 » : un couple solution (Bézout)
  const unknowns = ks.eq?.unknowns ?? [];
  const isAssign = (p: AChain) =>
    p.members.length === 2 &&
    strip(p.members[0]!).k === 'sym' &&
    unknowns.includes((strip(p.members[0]!) as { n: string }).n);
  if (unknowns.length && parts.every(isAssign)) return judgeRead(ks, base, parts);
  if (parts.length > 1)
    return {
      kind: 'syntax',
      ...base,
      title: 'Une égalité par ligne',
      message: unknowns.length
        ? 'Une seule égalité par ligne, sauf le couple « u = … ; v = … ».'
        : 'Une seule égalité par ligne.',
    };
  const ch = parts[0]!;
  if (ch.members.some((m) => symsOf(m).size))
    return {
      kind: 'unjustified',
      ...base,
      title: 'Avec des nombres',
      message: unknowns.length
        ? 'Cherche un couple avec l’algorithme d’Euclide : écris des divisions et des calculs avec des nombres, puis « u = … ; v = … ».'
        : 'Écris des divisions et des calculs avec des nombres.',
    };
  if (ks.primes) {
    const pv = judgePrimes(ks, base, ch.members);
    if (pv) return pv;
  }
  if (ch.members.some(hasLcm))
    return aside(base, `Ce PPCM n’est pas celui de la question : ${ks.target}.`);
  if (ch.members.some(hasGcd)) return judgeGcdChain(ks, base, ch.members);

  // un calcul entre nombres : tous les membres valent la même chose
  const vals = ch.members.map((m) => evalA(m));
  if (vals.some((x) => x === null))
    return { kind: 'syntax', ...base, title: 'Ça ne se lit pas', message: 'Termine ta ligne.' };
  if (vals.some((x) => x !== vals[0]))
    return trapOr(ks, base, {
      title: 'Le calcul ne tombe pas juste',
      message:
        ch.members.length === 2
          ? `${exprText(ch.members[1]!)} ne fait pas ${exprText(ch.members[0]!)} : vérifie ton calcul.`
          : 'Les membres de ta ligne ne sont pas tous égaux : vérifie ton calcul.',
    });
  if (ch.members.length === 2) {
    const div =
      divisionOf(ch.members[0]!, ch.members[1]!) ?? divisionOf(ch.members[1]!, ch.members[0]!);
    if (div) return judgeDivision(ks, base, div);
  }
  // une combinaison des restes : « 1 = 5 − 2 × 2 »
  const first = bareNumber(ch.members[0]!);
  const last = bareNumber(ch.members[ch.members.length - 1]!);
  const head = first !== null ? first : last;
  const rest = first !== null ? ch.members.slice(1) : ch.members.slice(0, -1);
  if (head !== null && ks.goal === 'bezout') {
    if (head === ks.d) return judgeCombination(ks, base, rest);
    const j = ks.idx.get(head);
    if (j !== undefined && j >= 2 && j <= ks.eu.m) return judgeIsolateOther(ks, base, j, rest);
  }
  if (rest.every((m) => bareNumber(m) !== null)) return nothingNew(base);
  return {
    kind: 'useless',
    ...base,
    title: 'Calcul juste',
    message:
      'Ce calcul est juste, mais il ne sert pas pour la question. Garde-le s’il t’aide : il ne rapporte pas de HORA.',
  };
}

/* ------------------------------ facteurs premiers (moteur v51) ------------------------------ */

/** le PGCD (le PPCM) de la question, écrit dans un membre */
function isTargetAtom(ks: KArithState, m: AExpr): boolean {
  const g = ks.goal === 'lcm' ? lcmOf(m) : gcdOf(m);
  return !!g && pairKey(absB(g.a), absB(g.b)) === pairKey(ks.a, ks.b);
}

/**
 * Une décomposition d'un des deux nombres : le nombre seul à un bout de la chaîne, un
 * produit de puissances d'entiers à l'autre (« 84 = 2 × 42 = 2² × 3 × 7 », dans un sens ou
 * l'autre). `pf` : la décomposition, si le produit n'a que des facteurs premiers.
 */
function decompositionOf(
  members: AExpr[],
  a: bigint,
  b: bigint,
): { n: bigint; end: AExpr; pf: Map<bigint, number> | null } | null {
  if (members.length < 2) return null;
  const first = bareNumber(members[0]!);
  const last = bareNumber(members[members.length - 1]!);
  const ends: [bigint | null, AExpr][] = [
    [first, members[members.length - 1]!],
    [last, members[0]!],
  ];
  for (const [n, end] of ends)
    if (n !== null && (n === a || n === b) && bareNumber(end) === null && writtenFactors(end))
      return { n, end, pf: primeProductOf(end) };
  return null;
}

function readDecomposition(
  text: string,
  a: bigint,
  b: bigint,
): [bigint, Map<bigint, number>] | null {
  const parts = readParts(text);
  if (!parts || parts.length !== 1) return null;
  const found = decompositionOf(parts[0]!.members, a, b);
  if (!found?.pf) return null;
  const vals = parts[0]!.members.map((m) => evalA(m));
  return vals.every((v) => v === found.n) ? [found.n, found.pf] : null;
}

/** une décomposition d'un des deux nombres, ou une ligne sur le PGCD (PPCM) ; null sinon */
function judgePrimes(ks: KArithState, base: Base, members: AExpr[]): KArithVerdict | null {
  if (members.some((m) => hasGcd(m) || hasLcm(m))) return judgePrimesTarget(ks, base, members);
  // « 2 × 3 × 7 = 42 » une fois « PGCD(84 ; 126) = 2 × 3 × 7 » écrit : calculer le PGCD
  if (ks.primesForm) {
    const want = ks.goal === 'lcm' ? ks.L : ks.d;
    const da = ks.dec.get(String(ks.a));
    const db = ks.dec.get(String(ks.b));
    const good = da && db ? combineFactors(da, db, ks.goal === 'lcm' ? 'lcm' : 'gcd') : null;
    const vals = members.map((m) => evalA(m));
    if (
      good &&
      vals.every((v) => v === want) &&
      members.some((m) => {
        const pf = bareNumber(m) === null ? primeProductOf(m) : null;
        return !!pf && sameFactors(pf, good);
      }) &&
      members.some((m) => bareNumber(m) === want)
    )
      return stepVerdict(
        base,
        ['compute'],
        { text: arithText(base.atoms), gcdKnown: true },
        !ks.concluded,
      );
  }
  const found = decompositionOf(members, ks.a, ks.b);
  if (!found) return null;
  const vals = members.map((m) => evalA(m));
  if (vals.some((v) => v === null)) return null;
  if (vals.some((v) => v !== found.n))
    return trapOr(ks, base, {
      title: 'Le produit ne tombe pas juste',
      message: `Ton produit ne vaut pas ${found.n} : vérifie ton calcul.`,
    });
  const text = arithText(base.atoms);
  // « 1001 = 7 × 143 » : une division de l'algorithme d'Euclide (le reste est nul)
  if (!found.pf && ks.goal === 'gcd' && members.length === 2) {
    const fs = writtenFactors(found.end);
    const other = found.n === ks.a ? ks.b : ks.a;
    if (fs?.length === 2 && fs.every((f) => f.e === 1) && fs.some((f) => f.b === other))
      return null;
  }
  if (!found.pf)
    return {
      kind: 'useless',
      ...base,
      title: 'Pas encore en facteurs premiers',
      message:
        'Ta ligne est juste, mais un facteur n’est pas un nombre premier : continue à décomposer. Tu peux la garder sans HORA.',
      amove: { text },
    };
  if (ks.dec.has(String(found.n)))
    return nothingNew(base, `La décomposition de ${found.n} est déjà sur ta feuille.`);
  return stepVerdict(base, ['decompose'], { text, dec: [found.n, found.pf] }, false);
}

type PItem =
  | { k: 'target' }
  | { k: 'num'; v: bigint }
  | { k: 'prod'; v: bigint; pf: Map<bigint, number> | null };

function judgePrimesTarget(ks: KArithState, base: Base, members: AExpr[]): KArithVerdict | null {
  const kind = ks.goal === 'lcm' ? 'lcm' : 'gcd';
  const items: PItem[] = [];
  for (const m of members) {
    if (isTargetAtom(ks, m)) {
      items.push({ k: 'target' });
      continue;
    }
    // un autre PGCD : l'algorithme d'Euclide (la chaîne des PGCD) ; un autre PPCM : à côté
    if (hasGcd(m) || hasLcm(m)) return null;
    const v = evalA(m);
    if (v === null) return null;
    const n = bareNumber(m);
    if (n !== null) items.push({ k: 'num', v: n });
    else items.push({ k: 'prod', v, pf: primeProductOf(m) });
  }
  if (!items.some((it) => it.k === 'target')) return null;
  const want = kind === 'lcm' ? ks.L : ks.d;
  const name = kind === 'lcm' ? 'PPCM' : 'PGCD';
  const da = ks.dec.get(String(ks.a));
  const db = ks.dec.get(String(ks.b));
  const valOf = (it: PItem): bigint => (it.k === 'target' ? want : it.v);
  const wrong = items.some((it) => valOf(it) !== want);
  // PGCD juste sans écriture en facteurs premiers : la chaîne se lit avec l'algorithme d'Euclide
  if (!wrong && kind === 'gcd' && !ks.primesForm && !items.some((it) => it.k === 'prod'))
    return null;
  if (wrong) {
    let title = `Pas le ${name}`;
    let message = `Ce n’est pas le ${name} de ${ks.a} et ${ks.b} : compare tes deux décompositions.`;
    const swapped = kind === 'gcd' ? ks.L : ks.d;
    if (swapped !== want && items.some((it) => it.k !== 'target' && it.v === swapped)) {
      title = kind === 'gcd' ? 'Les facteurs COMMUNS' : 'TOUS les facteurs';
      message =
        kind === 'gcd'
          ? `${swapped} est le PPCM. Pour le PGCD, on garde seulement les facteurs premiers communs aux deux nombres, avec le plus petit exposant.`
          : `${swapped} est le PGCD. Pour le PPCM, on garde tous les facteurs premiers des deux nombres, avec le plus grand exposant.`;
    }
    const prod = items.find((it) => it.k === 'prod' && it.pf) as
      | { k: 'prod'; v: bigint; pf: Map<bigint, number> }
      | undefined;
    if (prod && da && db) {
      const good = combineFactors(da, db, kind);
      const other = combineFactors(da, db, kind === 'gcd' ? 'lcm' : 'gcd');
      const sameKeys = prod.pf.size === good.size && [...good.keys()].every((p) => prod.pf.has(p));
      if (sameFactors(prod.pf, other)) {
        title = kind === 'gcd' ? 'Les facteurs COMMUNS' : 'TOUS les facteurs';
        message =
          kind === 'gcd'
            ? 'Pour le PGCD, on garde seulement les facteurs premiers communs aux deux nombres, avec le plus petit exposant.'
            : 'Pour le PPCM, on garde tous les facteurs premiers des deux nombres, avec le plus grand exposant.';
      } else if (sameKeys) {
        title = kind === 'gcd' ? 'Le plus PETIT exposant' : 'Le plus GRAND exposant';
        message =
          kind === 'gcd'
            ? 'Les bons facteurs premiers, mais pour le PGCD chacun prend le plus petit des deux exposants.'
            : 'Les bons facteurs premiers, mais pour le PPCM chacun prend le plus grand des deux exposants.';
      }
    }
    if (
      kind === 'lcm' &&
      title === `Pas le ${name}` &&
      items.some((it) => it.k !== 'target' && it.v === ks.a * ks.b)
    ) {
      title = 'Pas le plus petit';
      message = `${ks.a} × ${ks.b} est un multiple commun de ${ks.a} et ${ks.b}, mais pas le plus petit.`;
    }
    return trapOr(ks, base, { title, message });
  }
  const steps: StepKind[] = [];
  let jumped = false;
  let primesForm = false;
  const link = (P: PItem, N: PItem): void => {
    if (P.k !== 'target' && N.k === 'target') return link(N, P);
    if (P.k === 'target') {
      if (N.k === 'target') return;
      if (N.k === 'prod' && N.pf) {
        if (da && db) {
          steps.push(kind === 'lcm' ? 'lcmPrimes' : 'gcdPrimes');
          primesForm = true;
        } else jumped = true;
        return;
      }
      if (N.k === 'num' && (ks.primesForm || primesForm)) steps.push('compute');
      else jumped = true;
      return;
    }
    // deux écritures de la même valeur : un calcul (« 2 × 3 × 7 = 42 »)
    if (P.k === 'num' && N.k === 'num') return;
    if (P.k === 'prod' && N.k === 'prod' && P.pf && N.pf && sameFactors(P.pf, N.pf)) return;
    steps.push('compute');
  };
  for (let i = 0; i + 1 < items.length; i++) link(items[i]!, items[i + 1]!);
  const concludes = items.some((it) => it.k === 'num' && it.v === want);
  const final = concludes && !ks.concluded;
  const amove: AMove = {
    text: arithText(base.atoms),
    ...(primesForm ? { primesForm } : {}),
    ...(concludes ? { gcdKnown: true } : {}),
  };
  if (jumped) return jumpVerdict(base, amove, final);
  if (!steps.length) return nothingNew(base);
  return stepVerdict(base, steps, amove, final);
}

/* ------------------------------ divisions ------------------------------ */

interface Division {
  x: bigint;
  /** les deux facteurs écrits et le reste */
  f: [bigint, bigint];
  r: bigint;
}

/** « 1071 = 2 × 462 + 147 » (dans un ordre ou l'autre), « 147 = 7 × 21 » */
function divisionOf(left: AExpr, right: AExpr): Division | null {
  const x = bareNumber(left);
  if (x === null || x <= 0n) return null;
  const R = strip(right);
  const terms = R.k === 'add' ? R.t : [{ s: 1 as const, e: R }];
  if (terms.some((t) => t.s !== 1)) return null;
  const prod = (e: AExpr): [bigint, bigint] | null => {
    const s = strip(e);
    if (s.k !== 'mul' || s.f.length !== 2) return null;
    const a = bareNumber(s.f[0]!);
    const b = bareNumber(s.f[1]!);
    return a !== null && b !== null && a > 0n && b > 0n ? [a, b] : null;
  };
  if (terms.length === 1) {
    const f = prod(terms[0]!.e);
    return f ? { x, f, r: 0n } : null;
  }
  if (terms.length !== 2) return null;
  for (const [p, q] of [
    [0, 1],
    [1, 0],
  ] as const) {
    const f = prod(terms[p]!.e);
    const r = bareNumber(terms[q]!.e);
    if (f && r !== null) return { x, f, r };
  }
  return null;
}

function judgeDivision(ks: KArithState, base: Base, div: Division): KArithVerdict {
  // le diviseur : le facteur pour lequel le reste est plus petit (une division de la suite d'abord)
  const options = [
    { q: div.f[0], y: div.f[1] },
    { q: div.f[1], y: div.f[0] },
  ].filter((o) => div.r >= 0n && div.r < o.y);
  if (!options.length)
    return trapOr(ks, base, {
      title: 'Pas une division euclidienne',
      message:
        'Dans une division euclidienne, le reste est positif et plus petit que le diviseur : continue la division.',
    });
  const r = ks.eu.r;
  const k = options
    .map((o) => {
      for (let k = 1; k <= ks.eu.m; k++) if (r[k - 1] === div.x && r[k] === o.y) return k;
      return 0;
    })
    .find((k) => k > 0);
  if (!k)
    return aside(
      base,
      `Cette division est juste, mais elle ne fait pas partie de l’algorithme d’Euclide de ${ks.target}.`,
    );
  if (ks.divisions.has(k)) return nothingNew(base, 'Cette division est déjà sur ta feuille.');
  const amove: AMove = { text: arithText(base.atoms), divisions: [k] };
  if (k === 1 || ks.divisions.has(k - 1)) {
    const last = r[k + 1] === 0n;
    return stepVerdict(
      base,
      ['division'],
      amove,
      false,
      last ? ' Le reste est nul : l’algorithme s’arrête.' : '',
    );
  }
  return jumpVerdict(base, amove, false);
}

/* ------------------------------ PGCD ------------------------------ */

const pairKey = (a: bigint, b: bigint): string => (a >= b ? `${a}|${b}` : `${b}|${a}`);

/** la division k de la suite pour le couple (a ; b), 0 sinon */
function seqDivision(ks: KArithState, a: bigint, b: bigint): number {
  const big = a >= b ? a : b;
  const small = a >= b ? b : a;
  const r = ks.eu.r;
  for (let k = 1; k <= ks.eu.m; k++) if (r[k - 1] === big && r[k] === small) return k;
  return 0;
}

function judgeGcdChain(ks: KArithState, base: Base, members: AExpr[]): KArithVerdict {
  // chaque membre : un PGCD, ou un nombre
  const items: ({ k: 'gcd'; a: bigint; b: bigint } | { k: 'num'; v: bigint })[] = [];
  for (const m of members) {
    const g = gcdOf(m);
    if (g) {
      if (g.a < 0n || g.b < 0n || (g.a === 0n && g.b === 0n))
        return {
          kind: 'syntax',
          ...base,
          title: 'PGCD de deux entiers positifs',
          message: 'Le PGCD se calcule pour deux entiers positifs, pas tous les deux nuls.',
        };
      items.push({ k: 'gcd', a: g.a, b: g.b });
      continue;
    }
    const v = bareNumber(m);
    if (v === null)
      return {
        kind: 'syntax',
        ...base,
        title: 'Une chaîne de PGCD',
        message: 'Écris des PGCD ou un nombre, reliés par « = » : « PGCD(a ; b) = PGCD(b ; r) ».',
      };
    items.push({ k: 'num', v });
  }
  const valOf = (it: (typeof items)[number]) => (it.k === 'gcd' ? gcdB(it.a, it.b) : it.v);
  const v0 = valOf(items[0]!);
  if (items.some((it) => valOf(it) !== v0))
    return trapOr(ks, base, {
      title: 'Pas le même PGCD',
      message: 'Les membres de ta ligne ne sont pas égaux : vérifie tes divisions.',
    });
  // la ligne parle de l'algorithme de la question
  const relevant = items.some(
    (it) =>
      it.k === 'gcd' &&
      (seqDivision(ks, it.a, it.b) > 0 || pairKey(it.a, it.b) === pairKey(ks.d, 0n)),
  );
  if (!relevant)
    return aside(base, `Ce PGCD n’apparaît pas dans l’algorithme d’Euclide de ${ks.target}.`);

  const r = ks.eu.r;
  const allDivisions = (): boolean => {
    for (let k = 1; k <= ks.eu.m; k++) if (!ks.divisions.has(k)) return false;
    return true;
  };
  const steps: StepKind[] = [];
  let jumped = false;
  const isTarget = (it: (typeof items)[number]) =>
    it.k === 'gcd' && pairKey(it.a, it.b) === pairKey(r[0]!, r[1]!);
  const link = (P: (typeof items)[number], N: (typeof items)[number]): void => {
    if (P.k === 'num' && N.k === 'num') return;
    if (P.k === 'num' && N.k === 'gcd') return link(N, P);
    if (P.k !== 'gcd') return;
    const big = P.a >= P.b ? P.a : P.b;
    const small = P.a >= P.b ? P.b : P.a;
    if (N.k === 'gcd') {
      if (pairKey(N.a, N.b) === pairKey(P.a, P.b)) return; // échanger : rien de nouveau
      const rem = small === 0n ? null : big % small;
      // le sens « PGCD(b ; r) = PGCD(a ; b) » : on relit dans l'autre sens
      const nb = N.a >= N.b ? N.a : N.b;
      const ns = N.a >= N.b ? N.b : N.a;
      const nrem = ns === 0n ? null : nb % ns;
      if (rem !== null && pairKey(N.a, N.b) === pairKey(small, rem)) {
        const k = seqDivision(ks, big, small);
        if (k && ks.divisions.has(k)) steps.push('gcdStep');
        else jumped = true;
        return;
      }
      if (nrem !== null && pairKey(P.a, P.b) === pairKey(ns, nrem)) {
        const k = seqDivision(ks, nb, ns);
        if (k && ks.divisions.has(k)) steps.push('gcdStep');
        else jumped = true;
        return;
      }
      jumped = true;
      return;
    }
    // PGCD → nombre
    if (small === 0n) {
      if (N.v === big) steps.push('gcdZero');
      else jumped = true;
      return;
    }
    if (big % small === 0n && N.v === small) {
      const k = seqDivision(ks, big, small);
      if (k && ks.divisions.has(k)) steps.push('gcdDivides');
      else jumped = true;
      return;
    }
    if (isTarget(P) && N.v === ks.d && allDivisions()) {
      steps.push('gcdLast');
      return;
    }
    jumped = true;
  };
  for (let i = 0; i + 1 < items.length; i++) link(items[i]!, items[i + 1]!);

  const concludes = items.some(isTarget) && items.some((it) => it.k === 'num' && it.v === ks.d);
  const final = ks.goal === 'gcd' && concludes && !ks.concluded;
  const amove: AMove = { text: arithText(base.atoms), ...(concludes ? { gcdKnown: true } : {}) };
  if (jumped) return jumpVerdict(base, amove, final);
  if (!steps.length) return nothingNew(base);
  return stepVerdict(base, steps, amove, final);
}

/* ------------------------------ Bézout ------------------------------ */

/** une écriture regroupée a autant de termes que de nombres de la suite (mesure 0) */
function measureOf(e: AExpr, lin: LinForm): number {
  const nz = [...lin.values()].filter((c) => c !== 0n).length;
  return (hasParenSum(e) ? 2 : 0) + Math.max(0, topTerms(e) - nz);
}

const supportMax = (lin: LinForm): number =>
  Math.max(-1, ...[...lin.entries()].filter(([, c]) => c !== 0n).map(([i]) => i));

/** le reste r[k + 1] isolé de la division k : r[k−1] − q[k] × r[k] */
const isolateLin = (ks: KArithState, k: number): LinForm =>
  new Map([
    [k - 1, 1n],
    [k, -ks.eu.q[k]!],
  ]);

function judgeCombination(ks: KArithState, base: Base, members: AExpr[]): KArithVerdict {
  let cur = ks.cur;
  const steps: StepKind[] = [];
  let jumped = false;
  const combos: { lin: LinForm; measure: number }[] = [];
  const isolated: number[] = [];
  for (const E of members) {
    const reads = combinations(E, ks.idx);
    const has = (lin: LinForm) => reads.some((r) => linKey(r) === linKey(lin));
    if (!reads.length) {
      if (bareNumber(E) !== null) continue; // « 1 = 1 »
      return aside(
        base,
        'Ce calcul est juste, mais il n’utilise pas les restes de l’algorithme d’Euclide.',
      );
    }
    let next: { lin: LinForm; measure: number } | null = null;
    // 1. isoler le dernier reste non nul (écriture regroupée)
    const k = ks.eu.m - 1;
    if (k >= 1) {
      const iso = isolateLin(ks, k);
      if (has(iso) && measureOf(E, iso) === 0 && !ks.combos.has(linKey(iso))) {
        next = { lin: iso, measure: 0 };
        if (ks.divisions.has(k)) steps.push('isolate');
        else jumped = true;
        isolated.push(k);
      }
    }
    // 2. remplacer un reste par son expression
    if (!next && cur) {
      for (const [j, c] of cur.lin) {
        if (j < 2 || c === 0n) continue;
        const by = new Map([
          [j - 2, 1n],
          [j - 1, -ks.eu.q[j - 1]!],
        ]);
        const lin = substituteLin(cur.lin, j, by);
        if (!has(lin)) continue;
        const measure = measureOf(E, lin);
        next = { lin, measure };
        if (!ks.divisions.has(j - 1) || measure === 0) jumped = true;
        else steps.push('substitute');
        break;
      }
    }
    // 3. la même combinaison, écrite plus simplement
    if (!next && cur && has(cur.lin)) {
      const measure = measureOf(E, cur.lin);
      if (measure >= cur.measure) {
        if (members.length === 1) return nothingNew(base);
        continue;
      }
      steps.push(measure === 0 ? 'regroup' : 'expand');
      next = { lin: cur.lin, measure };
    }
    // 4. une autre combinaison juste : une étape sautée
    if (!next) {
      const best = [...reads].sort((a, b) => supportMax(a) - supportMax(b))[0]!;
      next = { lin: best, measure: measureOf(E, best) };
      jumped = true;
    }
    cur = next;
    combos.push(next);
  }
  if (!combos.length) return nothingNew(base);
  const amove: AMove = { text: arithText(base.atoms), combos, isolated };
  if (jumped) return jumpVerdict(base, amove, false);
  if (!steps.length) return nothingNew(base);
  return stepVerdict(base, steps, amove, false);
}

/** isoler un autre reste (« 2 = 17 − 3 × 5 ») : utile, sans être la combinaison du PGCD */
function judgeIsolateOther(
  ks: KArithState,
  base: Base,
  j: number,
  members: AExpr[],
): KArithVerdict {
  const k = j - 1;
  const iso = isolateLin(ks, k);
  if (members.length !== 1) return aside(base);
  const E = members[0]!;
  const reads = combinations(E, ks.idx);
  if (!reads.some((r) => linKey(r) === linKey(iso)) || measureOf(E, iso) !== 0) return aside(base);
  if (ks.isolated.has(k)) return nothingNew(base);
  const amove: AMove = { text: arithText(base.atoms), isolated: [k] };
  if (!ks.divisions.has(k)) return jumpVerdict(base, amove, false);
  return stepVerdict(base, ['isolate'], amove, false);
}

/** la valeur d'une inconnue lue sur une égalité de Bézout (forme sur r[0], r[1]) */
function readValue(ks: KArithState, lin: LinForm, u: string): bigint {
  const a = ks.eq!.coef.get(u)!;
  const i = ks.idx.get(absB(a))!;
  return (lin.get(i) ?? 0n) * (a < 0n ? -1n : 1n);
}

function satisfies(ks: KArithState, values: Map<string, bigint>): boolean {
  let s = 0n;
  ks.eq!.coef.forEach((c, u) => (s += c * values.get(u)!));
  return s === ks.eq!.rhs;
}

function judgeRead(ks: KArithState, base: Base, parts: AChain[]): KArithVerdict {
  const eq = ks.eq!;
  const values = new Map<string, bigint>();
  for (const p of parts) {
    const u = (strip(p.members[0]!) as { n: string }).n;
    const v = evalA(p.members[1]!);
    if (v === null || symsOf(p.members[1]!).size)
      return {
        kind: 'unjustified',
        ...base,
        title: 'Une valeur',
        message: `Donne la valeur de ${u} : un nombre entier.`,
      };
    if (values.has(u) && values.get(u) !== v)
      return trapOr(ks, base, {
        title: 'Deux valeurs',
        message: `${u} ne peut pas avoir deux valeurs.`,
      });
    values.set(u, v);
  }
  // déjà écrit ?
  const fresh = [...values].filter(([u, v]) => ks.assigned.get(u) !== v);
  if (!fresh.length) return nothingNew(base);
  if (fresh.some(([u]) => ks.assigned.has(u)))
    return trapOr(ks, base, {
      title: 'Déjà trouvée',
      message: 'Tu as déjà donné une autre valeur à cette inconnue.',
    });
  const all = new Map([...ks.assigned, ...values]);
  const complete = eq.unknowns.every((u) => all.has(u));
  const fromBezout = ks.bez.some((lin) =>
    [...values].every(([u, v]) => readValue(ks, lin, u) === v),
  );
  const amove: AMove = { text: arithText(base.atoms), assign: [...values] };
  if (complete) {
    if (!satisfies(ks, all)) {
      // les coefficients lus, mais sans tenir compte du signe ou de l'ordre
      const hint = (() => {
        for (const lin of ks.bez) {
          const swapped = eq.unknowns.every((u, i) => {
            const other = eq.unknowns[1 - i]!;
            return all.get(u) === readValue(ks, lin, other);
          });
          if (swapped)
            return `Tu as échangé les coefficients : ${eq.unknowns[0]} va avec ${absB(eq.coef.get(eq.unknowns[0]!)!)}.`;
          const unsigned = eq.unknowns.every(
            (u) => all.get(u) === (lin.get(ks.idx.get(absB(eq.coef.get(u)!))!) ?? 0n),
          );
          if (unsigned)
            return 'Attention au signe : regarde le signe devant chaque inconnue dans l’équation.';
        }
        return null;
      })();
      return trapOr(ks, base, {
        title: 'Ce couple ne va pas',
        message:
          hint ?? 'Ce couple ne vérifie pas l’équation : remplace les inconnues pour le vérifier.',
      });
    }
    const final = !ks.concluded;
    if (fromBezout) return stepVerdict(base, ['read'], amove, final);
    return jumpVerdict(base, amove, final);
  }
  if (fromBezout) return stepVerdict(base, ['read'], amove, false);
  return {
    kind: 'unjustified',
    ...base,
    title: 'D’où vient cette valeur ?',
    message: ks.bez.length
      ? 'Lis-la sur ton égalité de Bézout, celle qui ne contient plus que les deux nombres du départ.'
      : `Trouve d’abord une égalité « ${numText(ks.d)} = … » avec les deux nombres du départ, en remontant l’algorithme d’Euclide.`,
  };
}

/* ------------------------------ mémoire ------------------------------ */

export function karithCommit(ks: KArithState, v: KArithVerdict): void {
  const m = v.amove;
  if (!m) return;
  ks.written.add(m.text);
  ks.lines++;
  m.divisions?.forEach((k) => ks.divisions.add(k));
  m.isolated?.forEach((k) => ks.isolated.add(k));
  m.combos?.forEach((c) => {
    ks.cur = c;
    ks.combos.add(linKey(c.lin));
    if (supportMax(c.lin) <= 1 && !ks.bez.some((b) => linKey(b) === linKey(c.lin)))
      ks.bez.push(c.lin);
  });
  if (m.gcdKnown) ks.gcdKnown = true;
  if (m.dec) ks.dec.set(String(m.dec[0]), m.dec[1]);
  if (m.primesForm) ks.primesForm = true;
  m.assign?.forEach(([u, x]) => ks.assigned.set(u, x));
  if (m.final) ks.concluded = true;
  if (
    ks.goal === 'bezout' &&
    ks.eq &&
    ks.eq.unknowns.every((u) => ks.assigned.has(u)) &&
    satisfies(ks, ks.assigned)
  )
    ks.concluded = true;
  if ((ks.goal === 'gcd' || ks.goal === 'lcm') && ks.gcdKnown) ks.concluded = true;
}

export function karithGoalReached(ks: KArithState): boolean {
  return ks.concluded;
}

function readParts(text: string): AChain[] | null {
  try {
    return parseArithLine(arithLineAtoms(text)).parts;
  } catch {
    return null;
  }
}

/** la ligne est vraie : ses membres sont égaux ; un couple vérifie l'équation */
export function karithHolds(ks: KArithState, text: string): boolean {
  const parts = readParts(text);
  if (!parts || parts.some((p) => p.members.length < 2 || p.mod !== null)) return false;
  const unknowns = ks.eq?.unknowns ?? [];
  if (
    unknowns.length &&
    parts.every((p) => {
      const h = strip(p.members[0]!);
      return h.k === 'sym' && unknowns.includes(h.n);
    })
  ) {
    const all = new Map<string, bigint>();
    for (const p of parts) {
      const x = evalA(p.members[1]!);
      if (x === null) return false;
      all.set((strip(p.members[0]!) as { n: string }).n, x);
    }
    return unknowns.every((u) => all.has(u)) && satisfies(ks, all);
  }
  return parts.every((p) => {
    const vals = p.members.map((m) => evalA(m));
    return vals.every((x) => x !== null && x === vals[0]);
  });
}

/** la ligne donne la réponse : le PGCD de la question, ou un couple solution */
export function karithIsAnswer(ks: KArithState, text: string): boolean {
  const parts = readParts(text);
  if (!parts) return false;
  if (ks.goal === 'bezout') {
    const unknowns = ks.eq!.unknowns;
    if (
      !parts.every((p) => {
        const h = p.members.length === 2 ? strip(p.members[0]!) : null;
        return !!h && h.k === 'sym' && unknowns.includes(h.n);
      })
    )
      return false;
    return karithHolds(ks, text);
  }
  if (parts.length !== 1) return false;
  const ms = parts[0]!.members;
  if (ms.length < 2 || !ms.some((m) => isTargetAtom(ks, m))) return false;
  const last = bareNumber(ms[ms.length - 1]!);
  return last === (ks.goal === 'lcm' ? ks.L : ks.d) && karithHolds(ks, text);
}

/** une ligne déjà établie (le coup de pouce passe à la suivante) */
export function karithEstablished(ks: KArithState, text: string): boolean {
  if (ks.written.has(canonOf(text))) return true;
  const parts = readParts(text);
  if (!parts) return false;
  if (ks.primes && parts.length === 1) {
    const ms = parts[0]!.members;
    const found = decompositionOf(ms, ks.a, ks.b);
    if (found?.pf && ks.dec.has(String(found.n))) return true;
    if (
      ks.primesForm &&
      ms.some((m) => isTargetAtom(ks, m)) &&
      !ms.some((m) => bareNumber(m) !== null)
    )
      return true;
  }
  if (parts.length === 1 && parts[0]!.members.length === 2) {
    const [a, b] = parts[0]!.members as [AExpr, AExpr];
    const div = divisionOf(a, b) ?? divisionOf(b, a);
    if (div) {
      const r = ks.eu.r;
      for (let k = 1; k <= ks.eu.m; k++)
        if (r[k - 1] === div.x && (r[k] === div.f[0] || r[k] === div.f[1]) && ks.divisions.has(k))
          return true;
    }
  }
  if (ks.eq && parts.every((p) => p.members.length === 2)) {
    const assigns = parts.map((p) => {
      const h = strip(p.members[0]!);
      return h.k === 'sym' ? ([h.n, evalA(p.members[1]!)] as const) : null;
    });
    if (assigns.every((x) => x && x[1] !== null && ks.assigned.get(x[0]) === x[1])) return true;
  }
  return false;
}

const chipsOf = (atoms: string[]): Chip[] => atoms.map((a) => ({ atoms: [a], text: a }));

/** coup de pouce : la première ligne du corrigé pas encore écrite */
export function karithNextMove(
  ks: KArithState,
): { line: Line; text: string; atoms: string[]; label: string } | null {
  if (ks.concluded) return null;
  for (const t of ks.path) {
    if (karithEstablished(ks, t)) continue;
    const atoms = karithLineAtoms(t);
    const v = karithJudge(ks, chipsOf(atoms), { verify: true });
    return { line: karithLine(t), text: arithText(atoms), atoms, label: v.rule?.label ?? '' };
  }
  return null;
}

export function karithSolvePath(model: ScratchModel): {
  lines: { line: Line; text: string; atoms: string[]; v: KArithVerdict }[];
  reached: boolean;
} {
  const ks = createKArithState(model);
  const lines: { line: Line; text: string; atoms: string[]; v: KArithVerdict }[] = [];
  for (const t of ks.path) {
    const atoms = karithLineAtoms(t);
    const v = karithJudge(ks, chipsOf(atoms), { verify: true });
    if (v.kind !== 'step') break;
    karithCommit(ks, v);
    lines.push({ line: karithLine(t), text: arithText(atoms), atoms, v });
  }
  return { lines, reached: ks.concluded };
}

/* ------------------------------ palette ------------------------------ */

/** les opérations de l'arithmétique, dans l'ordre d'affichage */
export const ARITH_OPS = ['+', '−', '×', '(', ')', ';', '='];

/**
 * Les bouts de la feuille (D6 : l'énoncé et le corrigé, jamais les pièges ni les choix) :
 * les PGCD écrits, les inconnues, les nombres (triés), les opérations utiles.
 */
export function karithPaletteParts(model: ScratchModel): {
  gcds: string[];
  names: string[];
  numbers: string[];
  ops: string[];
} {
  const spec = model.kernel!;
  const texts = [spec.start, ...(spec.path ?? []), spec.answer];
  const atoms = texts.flatMap((t) => {
    try {
      return arithLineAtoms(t);
    } catch {
      return [];
    }
  });
  const gcds = [...new Set(atoms.filter((a) => isGcdAtom(a) || isLcmAtom(a)))];
  const names = [...new Set(atoms.filter((a) => /^[a-zA-Z][₀-₉]*$/.test(a)))];
  const nums = [...new Set(atoms.filter((a) => /^\d+$/.test(a)))];
  nums.sort((a, b) => (BigInt(a) < BigInt(b) ? -1 : BigInt(a) > BigInt(b) ? 1 : 0));
  const used = new Set(atoms);
  const ops = ARITH_OPS.filter((o) => used.has(o) || o === '=');
  // moteur v51 : les exposants des décompositions (« ² », « ³ »), avant « = »
  const sups = [...new Set(atoms.filter(isSupAtom))].sort(
    (x, y) => x.length - y.length || (x < y ? -1 : 1),
  );
  if (sups.length) ops.splice(ops.indexOf('='), 0, ...sups);
  return { gcds, names, numbers: nums, ops };
}
